// GroupDropdown — the shared group-by control: a "Group" trigger that reads
// "Group · <active field>" and holds the brand tint while grouping is on, over
// a menu of the caller's groupable fields plus a None row to turn it off
// (droppable via `clearable={false}` where grouping is always on). Only the
// open state lives here; the value is the caller's (null = ungrouped), so the
// same state can drive a DataTable's `grouping` prop or a board's column field.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from './Dropdown';
import Icon from './Icon';

export interface GroupOption<K extends string = string> {
  key: K;
  label: string;
}

/** Generic over the key union (e.g. a page's groupable-field type), so
 *  `onChange` hands back the caller's own key type without a cast. */
export interface GroupDropdownProps<K extends string = string> {
  options: GroupOption<K>[];
  /** The grouped field; null = grouping off. */
  value: K | null;
  onChange: (next: K | null) => void;
  /** Whether the None row renders (default true). A caller whose view always
   *  groups (e.g. a Kanban board) passes false and the effective field as
   *  `value` — `onChange` then never fires null. */
  clearable?: boolean;
  /** Panel edge to align with the trigger. Default 'start' (toolbar lead). */
  align?: 'start' | 'end';
  width?: number;
  /** Render the panel in a body portal to escape a clipping ancestor. */
  portal?: boolean;
}

const GroupDropdown = <K extends string = string>({
  options,
  value,
  onChange,
  clearable = true,
  align = 'start',
  width = 220,
  portal = false,
}: GroupDropdownProps<K>) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Fall back to the raw key so a value outside `options` still reads sanely.
  const activeLabel =
    value === null ? null : (options.find((o) => o.key === value)?.label ?? value);

  const pick = (key: K | null) => {
    onChange(key);
    setOpen(false);
  };

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align={align}
      width={width}
      portal={portal}
      className='group-dropdown'
      trigger={
        <button
          type='button'
          className={`group-dropdown__trigger${open ? ' is-open' : ''}${
            value !== null ? ' is-active' : ''
          }`}
          aria-haspopup='menu'
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name='layout-list' size={16} />
          {activeLabel === null ? t('groupby_label') : t('groupby_prefix', { value: activeLabel })}
          <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} />
        </button>
      }
    >
      {clearable && (
        <button
          type='button'
          className='app-dropdown__item'
          role='menuitemradio'
          aria-checked={value === null}
          onClick={() => pick(null)}
        >
          <span className='group-dropdown__label'>{t('groupby_none')}</span>
          {value === null && <Icon name='check' size={16} className='group-dropdown__check' />}
        </button>
      )}
      {options.map((option) => {
        const active = option.key === value;
        return (
          <button
            key={option.key}
            type='button'
            className='app-dropdown__item'
            role='menuitemradio'
            aria-checked={active}
            onClick={() => pick(option.key)}
          >
            <span className='group-dropdown__label'>{option.label}</span>
            {active && <Icon name='check' size={16} className='group-dropdown__check' />}
          </button>
        );
      })}
    </Dropdown>
  );
};

export default GroupDropdown;
